import AppLayout from '../AppLayout';
import GuestLayout from './GuestLayout';
import LoadingScreen from '../LoadingScreen';
import { useLayoutConfig } from '../../hooks/useLayoutConfig';
import type { LayoutSelectorProps } from '../../types/layout';
import SidebarNavigation from '../SidebarNavigation';
import MobileSideNavFrame from '../SidebarNavigation/MobileSideNavFrame';

interface DocsGuestAppShellProps {
  children: React.ReactNode;
}

function DocsGuestAppShell({ children }: DocsGuestAppShellProps) {
  return (
    <div className="app-container flex">
      <MobileSideNavFrame>
        <SidebarNavigation />
      </MobileSideNavFrame>

      <div id="main-content" className="flex-fluid outline-none">
        {children}
      </div>
    </div>
  );
}

/**
 * LayoutSelector is used for hybrid routes that need to switch between
 * guest and authenticated layouts based on user state.
 *
 * Hybrid routes include: /, /p/$username, /premium, /privacy, /tos, /help
 *
 * Authenticated-only routes should use AppLayout directly.
 */
function LayoutSelector({ children, config, title, className }: LayoutSelectorProps) {
  const {
    layoutConfig,
    user: currentUser,
    isLoading,
  } = useLayoutConfig({
    customConfig: config,
  });
  const isDocsPublicAppShellConfig =
    config?.type === 'app' && config.requiresAuth === false;

  // Show loading screen while determining user state
  if (isLoading && !isDocsPublicAppShellConfig) {
    return <LoadingScreen />;
  }

  // Render appropriate layout
  switch (layoutConfig.type) {
    case 'guest':
      return (
        <GuestLayout
          title={title}
          showAuthButtons={layoutConfig.showAuthButtons}
          className={className}
        >
          {children}
        </GuestLayout>
      );

    case 'hybrid':
      // For hybrid layouts, show guest layout if no user, app layout if authenticated
      if (!currentUser) {
        if (config?.type === 'app' && config.requiresAuth === false) {
          return <DocsGuestAppShell>{children}</DocsGuestAppShell>;
        }

        return (
          <GuestLayout
            title={title}
            showAuthButtons={layoutConfig.showAuthButtons}
            className={className}
          >
            {children}
          </GuestLayout>
        );
      }
      // For authenticated users in hybrid layout, use app layout
      return <AppLayout>{children}</AppLayout>;

    case 'app':
    default:
      // AppLayout expects user to be defined, show loading if not
      if (!currentUser) {
        if (isDocsPublicAppShellConfig) {
          return <DocsGuestAppShell>{children}</DocsGuestAppShell>;
        }
        return <LoadingScreen />;
      }

      // AppLayout now uses useCurrentUser hook directly
      return <AppLayout>{children}</AppLayout>;
  }
}

export default LayoutSelector;
